import { FC, ReactElement, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import {
  Tooltip,
  styled,
  TooltipProps,
  Box,
  Button,
  Typography,
  tooltipClasses,
  Zoom,
  Fade,
  Stack,
  useMediaQuery,
} from '@mui/material';
import styles from './styles.module.scss';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import CloseIcon from "@mui/icons-material/Close";

import { breakpoints } from '../utilities/responsiveBreakpoints';
import { useTranslates } from '../providers/translates';
import { useRedirectAndScroll } from '../hooks';
import { isVac } from 'utilities/is-vac';
import leafIcon from '../assets/icons/maple-leaf.svg';

interface WithTooltipProps {
  child: ReactElement<any, any>;
  url: string;
  name: string;
  id: number;
  dateOfBirth: number;
  dateOfPassing?: number;
  isDeceased?: boolean;
}

const styleDesktop = {
  position: 'absolute' as 'absolute',
  transform: 'translate(-50%, -50%)',
  width: 420,  
  boxShadow: 24,
  p: 1,
  outline: 'none',
  background: isVac() ? '#2a5e7f' : '#24476a',
};

const styleMobile = {
  width: '100%',
  background: isVac() ? '#2a5e7f' : '#24476a',
}

const WithTooltip: FC<WithTooltipProps> = ({
  child,
  url,
  name,
  id,
  dateOfBirth,
  dateOfPassing,
  isDeceased
}) => {
  const isSmallScreen = useMediaQuery("(max-width:1024px)");
  const { onlyRedirect } = useRedirectAndScroll({});

  const { translates: t } = useTranslates();

  const [isOpen, setIsOpen] = useState(false);
  const timeoutId = useRef<NodeJS.Timeout | undefined>(undefined);    
  const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))({
    [`& .${tooltipClasses.tooltip}`]: {
      background: isVac() ? '#2a5e7f' : '#24476a',
    },
  });

  const onView = (id: number) => {
    onlyRedirect(`quilts/${id}/memorial`);
  };

  return (
    <CustomWidthTooltip
      onMouseEnter={() => {
        if (!isMobile) {
          setIsOpen(true);
        }
      }}
      onClick={() => {
        if (isMobile) {
          if (!isOpen) {
            clearTimeout(timeoutId.current);

            timeoutId.current = setTimeout(() => {
              setIsOpen(false);
            }, 4000);
          }

          setIsOpen(prev => !prev);
        }
      }}
      PopperProps={{
        modifiers: [
          {
            name: "preventOverflow",
            options: {
              boundary: "window",
            },
          },
          {
            name: "customPosition",
            enabled: isSmallScreen,
            phase: "write",
            fn: ({ state }) => {
              if (state.elements.popper) {
                state.elements.popper.style.top = "0px";
                state.elements.popper.style.right = "0px";
                state.elements.popper.style.bottom = "auto";
                state.elements.popper.style.left = "50%";
                state.elements.popper.style.transform = `translate3d(-50%, ${state.rects.reference.y + state.rects.reference.height}px, 0px)`;
              }
            },
          },
        ],
      }}
      sx={{
        "& .MuiTooltip-tooltip": {
          maxWidth: 'unset'
        },
      }}
      TransitionComponent={
        document.documentElement.clientWidth > breakpoints.mobile.max ? Zoom : Fade
      }
      open={isOpen}
      placement={document.documentElement.clientWidth <= breakpoints.mobile.max ? 'top' : 'bottom'}
      title={
        <Box
        className={styles.infoBlock}
          sx={
            document.documentElement.clientWidth >= breakpoints.tablet.max
              ? styleDesktop
              : styleMobile
          }
          onMouseLeave={() => {
            if (!isMobile) {
              setIsOpen(false)
            }
          }}
        >
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              padding: '5px 0',
            }}
          >
            <CloseIcon 
              sx={{
                cursor: 'pointer'
              }}
              onClick={() => setIsOpen(false)} 
            />
          </Box>
          <img
            src={url}
            alt={name}
            className={styles.infoBlock_image}           
          />

          <Stack 
            direction="column" 
            display={'flex'}
            justifyContent={'space-between'}
            alignItems={'center'}
            width={'100%'}
            gap={'32px'}
            paddingBottom={'4px'}
          >
            <Stack 
              justifyContent="start" 
              alignItems="start" 
              mt={1} 
              ml={1} 
              width={'100%'}
            >
              <Typography
                id="modal-modal-title"
                variant="h6"
                component="h2"
                className={styles.infoBlock_title}                
                sx={{ 
                  textAlign: 'left', 
                  fontWeight: 700,
                  overflowWrap: 'anywhere'
                }}
              >
                {name}
              </Typography>
              <Box 
                sx={{ 
                  textAlign: 'left', 
                  position: 'relative',
                  overflowWrap: 'anywhere' 
                }}
              >
                <Typography component="span" id="modal-modal-description" className={styles.infoBlock_date}>
                  {dateOfBirth} - {dateOfPassing || 'present'}
                </Typography>
              </Box>
            </Stack>
            <Box 
              className={styles.infoBlock_lineblock}
            >
              
                {(isVac() && isDeceased) && (
                  <Box className={styles.leafIcon}>
                  <img 
                    src={leafIcon} 
                    alt='Leaf Icon' 
                    width={24} 
                  />
                  <small className={styles.leafIcontext}>{t?.home?.deceasedText ?? 'Denotes Veteran is deceased'}</small>
                  </Box>
                )}
                
              
              <Box
                style={{
                  textDecoration: 'none',
                  position: `${
                    document.documentElement.clientWidth <= 1024 ? 'static' : 'absolute'
                  }`,
                  right: '15px',
                  bottom: '15px',
                  background: '#57a0c1',
                  color: 'white',
                  borderRadius: '6px',
                  padding: '0',
                  margin: '0 0 0 auto'
                }}
              >
                <Button
                  sx={{
                    width: `${document.documentElement.clientWidth <= 1024 ? '100%' : 'inherit'}`,
                    color: 'white',
                    padding: '2px 8px',
                    paddingRight: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                  }}                  
                  onClick={() => onView(id)}
                >
                  <Typography
                    component="span"
                    className={styles.infoBlock_view}  
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      marginTop: '2px'
                    }}
                  >
                    {t?.carouselCard?.carouselCardBtnLabel || 'View'}
                  </Typography>

                  <ArrowRightIcon sx={{ fontSize: '34px', position: 'absolute', right: '-6px' }} />
                </Button>
              </Box>
            </Box>
          </Stack>
        </Box>
      }
    >
      {child}
    </CustomWidthTooltip>
  );
};

export default WithTooltip;
